import React, { useContext, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { ModaisColetaDomiciliarContext } from "../../../Context/ModaisColetaDomiciarContext";
import { api } from "../../../../../services/Api";

function normalizarResposta(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.logs)) {
    return data.logs;
  }

  if (Array.isArray(data?.coletaLogs)) {
    return data.coletaLogs;
  }

  if (Array.isArray(data?.resultado)) {
    return data.resultado;
  }

  return [];
}

function parseDados(dados) {
  if (!dados) {
    return null;
  }

  if (typeof dados === "object") {
    return dados;
  }

  if (typeof dados === "string") {
    try {
      return JSON.parse(dados);
    } catch (error) {
      return dados;
    }
  }

  return String(dados);
}

function formatarChave(chave) {
  if (!chave) {
    return "Campo";
  }

  const texto = String(chave).replaceAll("_", " ");

  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function formatarValor(valor) {
  if (valor === null || valor === undefined || valor === "") {
    return "-";
  }

  if (typeof valor === "object") {
    return JSON.stringify(valor);
  }

  return String(valor);
}

function ehObjetoDeAlteracoes(dados) {
  if (!dados || typeof dados !== "object" || Array.isArray(dados)) {
    return false;
  }

  const valores = Object.values(dados);

  if (valores.length === 0) {
    return false;
  }

  return valores.every(
    (valor) =>
      valor &&
      typeof valor === "object" &&
      !Array.isArray(valor) &&
      Object.hasOwn(valor, "de") &&
      Object.hasOwn(valor, "para")
  );
}

function DadosHistorico({ dados }) {
  const dadosProcessados = parseDados(dados);

  if (!dadosProcessados) {
    return null;
  }

  if (typeof dadosProcessados === "string") {
    return (
      <Typography
        variant="body2"
        color="text.primary"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {dadosProcessados}
      </Typography>
    );
  }

  if (Array.isArray(dadosProcessados)) {
    return (
      <Typography
        variant="body2"
        color="text.primary"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {JSON.stringify(dadosProcessados, null, 2)}
      </Typography>
    );
  }

  if (ehObjetoDeAlteracoes(dadosProcessados)) {
    return (
      <TableContainer
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "background.paper",
        }}
      >
        <Table size="small" aria-label="Tabela de alterações">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ fontWeight: 600, fontSize: "0.68rem", py: 0.4, px: 1 }}
              >
                Campo
              </TableCell>
              <TableCell
                sx={{ fontWeight: 600, fontSize: "0.68rem", py: 0.4, px: 1 }}
              >
                Antes
              </TableCell>
              <TableCell
                sx={{ fontWeight: 600, fontSize: "0.68rem", py: 0.4, px: 1 }}
              >
                Depois
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(dadosProcessados).map(([chave, valor]) => (
              <TableRow key={chave}>
                <TableCell sx={{ verticalAlign: "top" }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatarChave(chave)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  <Typography
                    variant="caption"
                    color="text.primary"
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {formatarValor(valor.de)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  <Typography
                    variant="caption"
                    color="text.primary"
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {formatarValor(valor.para)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 0.4 }}>
      {Object.entries(dadosProcessados).map(([chave, valor]) => (
        <Box
          key={chave}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "140px 1fr" },
            gap: 0.4,
            borderBottom: 1,
            borderColor: "divider",
            pb: 0.4,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {formatarChave(chave)}
          </Typography>
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {formatarValor(valor)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function ModalHistorico({ data, hora }) {
  const { enqueueSnackbar } = useSnackbar();
  const { modalHistorico, setModalHistorico } = useContext(
    ModaisColetaDomiciliarContext
  );
  
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);

  const rastreador =
    data && hora
      ? `${data}-${hora}`
      : "";

  function formatarDataHora(valor) {
    if (!valor) {
      return "Data não informada";
    }

    const data = new Date(valor);

    if (Number.isNaN(data.getTime())) {
      return String(valor);
    }

    return data.toLocaleString("pt-BR");
  }

  useEffect(() => {
    async function carregarHistorico() {
      if (!rastreador) {
        setHistorico([]);
        return;
      }

      try {
        setLoading(true);

        const { data } = await api.get("/v2/coleta/coletaLog", {
          params: {
            rastreador,
            limit: 50,
          },
        });

        setHistorico(normalizarResposta(data));
      } catch (error) {
        setHistorico([]);
        enqueueSnackbar("Falha ao carregar histórico", { variant: "error" });
      } finally {
        setLoading(false);
      }
    }

    if (modalHistorico) {
      carregarHistorico();
    }
  }, [enqueueSnackbar, modalHistorico, rastreador]);

  return (
    <Dialog
      open={modalHistorico}
      onClose={() => setModalHistorico(false)}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ color: "text.primary" }}>Histórico</DialogTitle>
      <DialogContent dividers sx={{ color: "text.primary" }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Data e Horário da Coleta: {rastreador || "Não informado"}
          </Typography>
        </Box>

        {loading && (
          <Box
            sx={{
              py: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && historico.length === 0 && (
          <Typography variant="body2" color="text.primary">
            Sem histórico para exibir.
          </Typography>
        )}

        {!loading && historico.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {historico.map((item, index) => {
              const identificador =
                item.id || item._id || `${item.usuario || "usuario"}-${index}`;

              return (
                <Box
                  key={identificador}
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    gap: 2,
                    pb: index < historico.length - 1 ? 2.5 : 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 20,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        mt: 0.6,
                        flexShrink: 0,
                      }}
                    />
                    {index < historico.length - 1 && (
                      <Box
                        sx={{
                          mt: 0.5,
                          width: 2,
                          flex: 1,
                          bgcolor: "divider",
                          minHeight: 36,
                        }}
                      />
                    )}
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      bgcolor: "background.paper",
                      color: "text.primary",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 0.75,
                        flexWrap: "wrap",
                      }}
                    >
                         <Typography variant="caption" color="text.secondary">
                        {item.usuario || "Não informado"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatarDataHora(
                          item.criado_em ||
                            item.createdAt ||
                            item.data_criacao ||
                            item.data
                        )}
                      </Typography>
                     
                    </Box>
                    <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                      {item.acao || "Ação não informada"}
                    </Typography>
                    {item.dados && (
                      <Box
                        sx={{
                          mt: 0.5,
                          p: 0.5,
                          bgcolor: "background.default",
                          borderRadius: 1,
                          border: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Dados:
                        </Typography>
                        <Box sx={{ mt: 0.25 }}>
                          <DadosHistorico dados={item.dados} />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setModalHistorico(false)}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}